import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRundownRows } from '../../hooks/useRundownRows';
import { useShowStore } from '../../stores/showStore';
import { useSettingsStore, effectiveRelativeTimes } from '../../stores/settingsStore';
import { useClockTick } from '../../contexts/ClockTickContext.jsx';
import { cuesAPI } from '../../lib/api';
import { TYPE_META } from '../rundown/CueTypeBadge';
import InTime from '../rundown/InTime';
import { useCan } from '../../hooks/useCan';

// ── Formatting helpers ────────────────────────────────────────────────────────
function pad2(n) { return String(n).padStart(2, '0'); }

function formatClock(date) {
  if (!date) return '--:--:--';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function formatHMS(seconds) {
  if (seconds == null || isNaN(seconds)) return '--:--:--';
  const abs = Math.abs(Math.round(seconds));
  return `${pad2(Math.floor(abs / 3600))}:${pad2(Math.floor((abs % 3600) / 60))}:${pad2(abs % 60)}`;
}

// Duration as MM:SS when under an hour, else H:MM:SS (group meta line).
function formatDur(seconds) {
  const abs = Math.abs(Math.round(seconds || 0));
  const h = Math.floor(abs / 3600), m = Math.floor((abs % 3600) / 60), s = abs % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
}

function parseHMS(str) {
  if (!str) return 0;
  const parts = str.trim().split(':').map(n => parseInt(n) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parseInt(str) || 0;
}

// Spine node colour for a cue type — SEGMENT is neutral (faint); others take
// their accent token; past cues are always the neutral border2.
function nodeColour(type) {
  if (type === 'SEGMENT') return 'var(--faint)';
  const meta = TYPE_META[type];
  return meta && meta.token ? `var(${meta.token})` : 'var(--faint)';
}

// Row-kind estimate for the virtualiser (real heights are measured on mount).
function estimateFor(kind) {
  switch (kind) {
    case 'past': return 30;
    case 'glabel': return 56;
    case 'live': return 168;
    case 'next': return 72;
    case 'divider': return 44;
    default: return 44;
  }
}

function kindOf(row) {
  if (row.status === 'on-air') return 'live';
  if (row.status === 'next') return 'next';
  if (row.status === 'group') return 'glabel';
  if (row.status === 'complete' || row.status === 'skipped') return 'past';
  if (row.status === 'divider') return 'divider';
  return 'future';
}

// Whether inline edits and row actions are locked: a guest without production
// access may not rename cues, change timings, or clone and delete them.
const SpineLocked = React.createContext(false);

// ── Inline quick-edit (label / duration) — kept working in modern per spec ────
function SpineEditable({ value, display, onSave, style, className, placeholder, title }) {
  const locked = React.useContext(SpineLocked);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef(null);
  function start(e) { e.stopPropagation(); setDraft(value ?? ''); setEditing(true); setTimeout(() => ref.current?.select(), 0); }
  function save() { setEditing(false); const t = draft.trim(); if (t !== (value ?? '').toString()) onSave(t); }
  if (editing) {
    return (
      <input
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        style={{ ...style, background: 'rgba(91,138,222,.14)', border: '1px solid var(--blue)', borderRadius: 5, padding: '2px 6px', color: 'var(--text)', outline: 'none', font: 'inherit', minWidth: 0 }}
        className={className}
      />
    );
  }
  return (
    <span onClick={locked ? undefined : start} style={{ ...style, cursor: locked ? 'default' : 'text' }} className={className} title={locked ? undefined : (title || 'Click to edit')}>
      {display ?? (value || placeholder)}
    </span>
  );
}

// Schedule drift vs wall clock (green ahead / red behind), ticking live.
function DriftCell({ inTime, style }) {
  useClockTick();
  if (!inTime) return <span style={{ ...style, color: 'var(--faint)' }}>--:--:--</span>;
  const diff = Math.round((inTime.getTime() - Date.now()) / 1000);
  const sign = diff >= 0 ? '+' : '-';
  return <span style={{ ...style, color: diff >= 0 ? 'var(--green)' : 'var(--over)' }}>{sign}{formatHMS(diff)}</span>;
}

// The on-air card's live elapsed / progress / remaining, ticking each second.
function LiveTimers({ plannedDuration }) {
  useClockTick();
  const onAirStartTime = useShowStore(s => s.onAirStartTime);
  const elapsedSec = onAirStartTime ? Math.max(0, Math.floor((Date.now() - onAirStartTime) / 1000)) : 0;
  const remainingSec = (plannedDuration || 0) - elapsedSec;
  const pct = plannedDuration > 0 ? Math.min(100, Math.max(0, (elapsedSec / plannedDuration) * 100)) : 0;
  const mono = { fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace" };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
      <span style={{ ...mono, fontSize: 16, fontWeight: 400, color: 'var(--green)' }}>{formatDur(elapsedSec)}</span>
      <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--surface3)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--live)' }} />
      </div>
      <span style={{ ...mono, fontSize: 16, fontWeight: 400, color: remainingSec < 0 ? 'var(--live)' : 'var(--text)' }}>
        {remainingSec < 0 ? '+' : '−'}{formatDur(remainingSec)}
      </span>
      <span style={{ ...mono, fontSize: 14, color: 'var(--faint)' }}>of {formatDur(plannedDuration)}</span>
    </div>
  );
}

const mono = { fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace" };

export default function ProducerViewModern({ showId, show }) {
  const {
    rows, displayedCues, onAirCue, cues, inTimeById,
  } = useRundownRows(show);
  const relativeTimes = useSettingsStore(s => s.relativeTimes);
  const uiStyle = useSettingsStore(s => s.uiStyle);
  const followOnAir = useSettingsStore(s => s.followOnAir);
  const relativeOn = effectiveRelativeTimes(relativeTimes, uiStyle);
  const canProduce = useCan('production:edit');
  const { toggleGroupCollapse, updateCue } = useShowStore.getState();

  const scrollRef = useRef(null);

  const updateField = useCallback(async (cue, field, value) => {
    try {
      const r = await cuesAPI.update(showId, cue.id, { [field]: value });
      updateCue(r.data);
    } catch (err) {
      console.error('Failed to update cue:', err);
    }
  }, [showId, updateCue]);

  async function handleClone(cue) {
    try { await cuesAPI.clone(showId, cue.id); } catch (err) { console.error('Clone failed:', err); }
  }
  async function handleDelete(cue) {
    if (!confirm(`Delete cue "${cue.label}"?`)) return;
    try { await cuesAPI.delete(showId, cue.id); } catch (err) { console.error('Delete failed:', err); }
  }

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => estimateFor(kindOf(rows[i])),
    overscan: 8,
    scrollPaddingStart: 28,
  });

  // Follow live: recentre on the on-air cue when it changes.
  useEffect(() => {
    if (!onAirCue || !followOnAir) return;
    const idx = displayedCues.findIndex(c => c.id === onAirCue.id);
    if (idx !== -1) virtualizer.scrollToIndex(idx, { align: 'center', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAirCue?.id]);

  // Footer: remaining upcoming cues + projected end adjusted for current drift.
  const pendingCount = cues.filter(c => c.status === 'pending').length;
  const lastWithIn = [...displayedCues].reverse().find(c => inTimeById.get(c.id));
  let endText = null;
  if (lastWithIn) {
    const inT = inTimeById.get(lastWithIn.id);
    const scheduledEnd = new Date(inT.getTime() + (lastWithIn.planned_duration || 0) * 1000);
    // Current drift from the next pending cue (positive = ahead of schedule).
    const nextPending = cues.find(c => c.status === 'pending');
    const driftSec = nextPending && inTimeById.get(nextPending.id)
      ? Math.round((inTimeById.get(nextPending.id).getTime() - Date.now()) / 1000)
      : 0;
    const projectedEnd = new Date(scheduledEnd.getTime() - driftSec * 1000);
    const word = driftSec < 0 ? 'late' : 'early';
    endText = `ends ${pad2(projectedEnd.getHours())}:${pad2(projectedEnd.getMinutes())}${driftSec !== 0 ? ` ${word}` : ''} at current drift`;
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <SpineLocked.Provider value={!canProduce}>
    <div ref={scrollRef} className="h-full overflow-auto" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div style={{ maxWidth: 1060, margin: '0 auto', padding: '28px 32px 80px', position: 'relative' }}>
        {/* The spine */}
        <div style={{ position: 'absolute', left: 39, top: 28, bottom: 80, width: 2, background: 'var(--border)' }} />

        {rows.length === 0 ? (
          <div style={{ color: 'var(--faint)', padding: '40px 28px' }}>No cues yet.</div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualItems.map(vi => {
              const row = rows[vi.index];
              return (
                <div
                  key={row.id}
                  data-index={vi.index}
                  ref={virtualizer.measureElement}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${vi.start}px)` }}
                >
                  <SpineRow
                    row={row}
                    relativeOn={relativeOn}
                    onToggleGroup={toggleGroupCollapse}
                    onEditField={updateField}
                    onClone={handleClone}
                    onDelete={handleDelete}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        {rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '26px 0 0 28px', color: 'var(--faint)', fontSize: 12 }}>
            <span>{pendingCount} more cue{pendingCount !== 1 ? 's' : ''}</span>
            {endText && <><span style={mono}>·</span><span>{endText}</span></>}
          </div>
        )}
      </div>
    </div>
    </SpineLocked.Provider>
  );
}

// ── One spine row, dispatched by kind ─────────────────────────────────────────
const SpineRow = React.memo(function SpineRow({ row, relativeOn, onToggleGroup, onEditField, onClone, onDelete }) {
  const { cue, status } = row;
  const kind = kindOf(row);
  const type = cue.cue_type;
  const locked = React.useContext(SpineLocked);

  const editLabel = (v) => onEditField(cue, 'label', v);
  const editDur = (v) => onEditField(cue, 'planned_duration', Math.max(0, parseHMS(v)));

  // ── Past: compressed to a 30px breath ──
  if (kind === 'past') {
    const skipped = status === 'skipped';
    return (
      <div className="group" style={{ display: 'flex', alignItems: 'center', gap: 14, height: 30, paddingLeft: 28, position: 'relative' }}>
        <span style={{ position: 'absolute', left: 5, width: 6, height: 6, borderRadius: '50%', background: 'var(--border2)', border: '2px solid var(--bg)' }} />
        <span style={{ ...mono, fontSize: 13, color: 'var(--faint)', width: 42 }}>{row.displayNumber}</span>
        <SpineEditable
          value={cue.label}
          onSave={editLabel}
          placeholder="Label…"
          style={{ flex: 1, fontSize: 13, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: skipped ? 'line-through' : 'none' }}
        />
        <span style={{ fontSize: 13, color: skipped ? 'var(--faint)' : 'var(--green)' }}>{skipped ? '»' : '✓'}</span>
      </div>
    );
  }

  // ── Group: a margin note with a ring node (click to collapse/expand) ──
  if (kind === 'glabel') {
    const meta = `${row.childrenCount} cue${row.childrenCount !== 1 ? 's' : ''} · ${formatDur(row.groupDuration || 0)}${row.inTime ? ` · began ${formatClock(row.inTime)}` : ''}`;
    return (
      <div
        onClick={() => onToggleGroup(cue.id)}
        style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '22px 0 8px 28px', position: 'relative', cursor: 'pointer' }}
        title={row.isGroupCollapsed ? 'Expand group' : 'Collapse group'}
      >
        <span style={{ position: 'absolute', left: 2, top: 24, width: 12, height: 12, borderRadius: '50%', background: 'var(--bg)', border: '2px solid var(--t-group)' }} />
        <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '.14em', color: 'var(--t-group)', textTransform: 'uppercase' }}>
          {cue.label}{row.isGroupCollapsed ? ' ▸' : ''}
        </span>
        <span style={{ ...mono, fontSize: 13, color: 'var(--faint)' }}>{meta}</span>
      </div>
    );
  }

  // ── Divider (GO / §): a hard marker on the spine ──
  if (kind === 'divider') {
    const meta = TYPE_META[type];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, height: 44, paddingLeft: 28, position: 'relative' }}>
        <span style={{ position: 'absolute', left: 3, width: 12, height: 12, borderRadius: '50%', background: 'var(--text)', border: '2px solid var(--bg)' }} />
        <span style={{ padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 500, letterSpacing: '.06em', background: 'var(--text)', color: 'var(--bg)' }}>{meta?.short || '§'}</span>
        <SpineEditable value={cue.label} onSave={editLabel} placeholder="Label…" style={{ flex: 1, fontSize: 15, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} />
      </div>
    );
  }

  // ── Live: the only loud element ──
  if (kind === 'live') {
    const hasVmix = (cue.cue_actions || []).some(a => a.type === 'vmix');
    const hasSingular = (cue.cue_actions || []).some(a => a.type === 'singular');
    return (
      <div style={{ margin: '10px 0 10px 28px', position: 'relative', background: 'var(--live-bg)', border: '1px solid color-mix(in srgb, var(--live) 35%, transparent)', borderRadius: 10, padding: '18px 22px 16px' }}>
        <span className="on-air-pulse" style={{ position: 'absolute', left: -32, top: 24, width: 14, height: 14, borderRadius: '50%', background: 'var(--live)', border: '3px solid var(--bg)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span className="on-air-pulse" style={{ padding: '3px 10px', borderRadius: 5, fontSize: 10, fontWeight: 500, letterSpacing: '.06em', background: 'var(--live)', color: '#fff' }}>ON AIR</span>
          <span style={{ ...mono, fontSize: 13, color: 'var(--dim)' }}>{row.displayNumber}</span>
          <span style={{ flex: 1 }} />
          <span style={{ display: 'flex', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: hasVmix ? 'var(--green)' : 'var(--surface3)' }} />
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: hasSingular ? 'var(--violet)' : 'var(--surface3)' }} />
          </span>
        </div>
        <SpineEditable
          value={cue.label}
          onSave={editLabel}
          placeholder="Label…"
          style={{ display: 'block', fontSize: 23, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 14, color: 'var(--text)' }}
        />
        <LiveTimers plannedDuration={cue.planned_duration || 0} />
      </div>
    );
  }

  // ── Next: amber row ──
  if (kind === 'next') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, height: 52, position: 'relative', background: 'var(--next-bg)', borderRadius: 8, margin: '0 0 0 28px', padding: '0 18px' }}>
        <span style={{ position: 'absolute', left: -26, width: 10, height: 10, borderRadius: '50%', background: 'var(--next)', border: '2px solid var(--bg)' }} />
        <span style={{ padding: '2px 9px', borderRadius: 5, fontSize: 10, fontWeight: 500, letterSpacing: '.06em', color: 'var(--next)', border: '1px solid var(--next)' }}>NEXT</span>
        <span style={{ ...mono, fontSize: 13, color: 'var(--dim)', width: 34 }}>{row.displayNumber}</span>
        <SpineEditable value={cue.label} onSave={editLabel} placeholder="Label…" style={{ flex: 1, fontSize: 16, fontWeight: 400, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} />
        <InTime inTime={row.inTime} relative={relativeOn} style={{ ...mono, fontSize: 14, fontWeight: 400, color: 'var(--next)' }} />
        <SpineEditable value={String(cue.planned_duration || 0)} display={formatDur(cue.planned_duration || 0)} onSave={editDur} style={{ ...mono, fontSize: 14, color: 'var(--faint)' }} title="Click to edit duration" />
      </div>
    );
  }

  // ── Future: node · num · optional type tag · label · times / hover actions ──
  const node = nodeColour(type);
  const isDisabled = cue.enabled === false;
  const showTag = type !== 'SEGMENT';
  return (
    <div className="group" style={{ display: 'flex', alignItems: 'center', gap: 14, height: 44, paddingLeft: 28, position: 'relative', opacity: isDisabled ? 0.5 : 1 }}>
      <span style={{ position: 'absolute', left: 4, width: 8, height: 8, borderRadius: '50%', background: node, border: '2px solid var(--bg)' }} />
      <span style={{ ...mono, fontSize: 13, color: 'var(--faint)', width: 42 }}>{row.displayNumber}</span>
      {showTag && <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '.06em', color: node }}>{TYPE_META[type]?.short || ''}</span>}
      <SpineEditable value={cue.label} onSave={editLabel} placeholder="Label…" style={{ flex: 1, fontSize: 15, fontWeight: 400, color: isDisabled ? 'var(--dim)' : 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} />

      {/* Times (default) — hidden on hover */}
      <span className={locked ? undefined : 'group-hover:opacity-0 transition-opacity'} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <InTime inTime={row.inTime} relative={relativeOn} style={{ ...mono, fontSize: 14, color: 'var(--dim)', width: 74, textAlign: 'right', display: 'inline-block' }} />
        <span style={{ ...mono, fontSize: 14, color: 'var(--faint)', width: 64, textAlign: 'right' }}>{formatDur(cue.planned_duration || 0)}</span>
        <DriftCell inTime={row.inTime} style={{ ...mono, fontSize: 14, width: 72, textAlign: 'right', display: 'inline-block' }} />
      </span>

      {/* Actions (on hover) — overlaid at the right, replacing the times */}
      {!locked && <span
        className="opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ position: 'absolute', right: 0, display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: 'var(--dim)', paddingRight: 2, background: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        <button onClick={() => onEditField(cue, 'enabled', isDisabled)} title={isDisabled ? 'Enable cue' : 'Disable cue'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDisabled ? 'var(--faint)' : 'var(--green)' }}>⏻</button>
        <button onClick={() => onClone(cue)} title="Clone" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)' }}>⧉</button>
        <button onClick={() => onDelete(cue)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)' }}>✕</button>
      </span>}
    </div>
  );
});
